import React, { useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';

const EventManager = ({ classId, classData, fetchClassData }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const [newEvent, setNewEvent] = useState({
    title: '',
    year: '',
    description: '',
    summary: '',
    category: '',
    links: [''],
    disableAI: false,
    bulletPointsSummary: false,
  });

  const openPopup = async (event) => {
    setSelectedEvent(event);
    setInsights(null);
    if (event.disableAI) return;
    setLoadingSummary(true);
    try {
      const res = await axios.post('https://amora-ai-service.onrender.com/research-assistant', {
        title: event.title,
        description: event.description,
      });
      setInsights(res.data);
    } catch {
      setInsights({ ai_summary: '⚠️ Failed to fetch AI summary.' });
    }
    setLoadingSummary(false);
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setEditForm({
      ...event,
      links: event.links || [''],
      bulletPointsSummary: event.bulletPointsSummary || false,
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`https://amora-backend.onrender.com/api/classes/${classId}/events/${editForm._id}`, editForm);
      setEditingEvent(null);
      setEditForm(null);
      await fetchClassData();
    } catch (err) {
      console.error('❌ Failed to update event:', err);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`https://amora-backend.onrender.com/api/classes/${classId}/events`, newEvent);
      setNewEvent({
        title: '',
        year: '',
        description: '',
        summary: '',
        category: '',
        links: [''],
        disableAI: false,
        bulletPointsSummary: false,
      });
      setShowAddForm(false);
      await fetchClassData();
    } catch (err) {
      console.error('Failed to create event:', err);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event from the class?')) return;
    try {
      await axios.delete(`https://amora-backend.onrender.com/api/classes/${classId}/events/${eventId}`);
      await fetchClassData();
    } catch (err) {
      console.error('Failed to delete event:', err);
    }
  };

  const handleAddToDeck = async (event, deckTitle) => {
    try {
      await axios.post(`https://amora-backend.onrender.com/api/classes/${classId}/decks/${deckTitle}/events`, event);
      alert(`✅ Added to deck: ${deckTitle}`);
    } catch (err) {
      console.error('Failed to add to deck:', err);
    }
  };

  if (!classData) return null;

  return (
    <div className="text-white">
      <h2 className="text-2xl font-bold mb-6 text-purple-400">📅 Manage Events</h2>

      {/* Add Form */}
      <div className="flex justify-center mb-10">
        <div className="w-full max-w-2xl">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg mb-6 font-semibold w-full"
          >
            {showAddForm ? '➖ Cancel' : '➕ Create New Event'}
          </button>

          <AnimatePresence>
            {showAddForm && (
              <motion.form
                onSubmit={handleCreateEvent}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4 }}
                className="bg-gray-800 p-6 rounded-xl space-y-4"
              >
                <input type="text" value={newEvent.title} onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })} placeholder="Title" className="w-full px-4 py-2 rounded bg-gray-700 border border-gray-600" required />
                <input type="number" value={newEvent.year} onChange={(e) => setNewEvent({ ...newEvent, year: e.target.value })} placeholder="Year" className="w-full px-4 py-2 rounded bg-gray-700 border border-gray-600" required />
                <input type="text" value={newEvent.category} onChange={(e) => setNewEvent({ ...newEvent, category: e.target.value })} placeholder="Category" className="w-full px-4 py-2 rounded bg-gray-700 border border-gray-600" />
                <textarea value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} placeholder="Short Description" className="w-full px-4 py-2 rounded bg-gray-700 border border-gray-600" rows="2" required />
                <textarea value={newEvent.summary} onChange={(e) => setNewEvent({ ...newEvent, summary: e.target.value })} placeholder="Summary (Optional)" className="w-full px-4 py-2 rounded bg-gray-700 border border-gray-600" rows="4" />
                {newEvent.links.map((link, idx) => (
                  <input key={idx} value={link} onChange={(e) => {
                    const links = [...newEvent.links];
                    links[idx] = e.target.value;
                    setNewEvent({ ...newEvent, links });
                  }} placeholder={`Link ${idx + 1}`} className="w-full mt-2 px-4 py-2 rounded bg-gray-700 border border-gray-600" />
                ))}
                <button type="button" onClick={() => setNewEvent(prev => ({ ...prev, links: [...prev.links, ''] }))} className="text-blue-400 hover:underline text-sm">➕ Add another link</button>
                <label className="flex items-center gap-2 text-sm mt-2">
                  <input type="checkbox" checked={newEvent.disableAI} onChange={(e) => setNewEvent({ ...newEvent, disableAI: e.target.checked })} /> Turn off AI summary
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={newEvent.bulletPointsSummary} onChange={(e) => setNewEvent({ ...newEvent, bulletPointsSummary: e.target.checked })} /> Summarize with Bullet Points
                </label>
                <button type="submit" className="w-full bg-green-600 hover:bg-green-700 py-2 rounded-lg font-semibold mt-4">✅ Submit Event</button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Event Cards */}
      <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
        {classData.events.map((event) => (
          <div
            key={event._id}
            className="flex flex-col bg-gray-800 text-white p-4 rounded-xl shadow hover:bg-gray-700 transition relative cursor-pointer"
            onClick={() => openPopup(event)}
          >
            <h4 className="text-lg font-bold text-blue-300">{event.title}</h4>
            <p className="text-sm text-gray-400">📅 {event.year}</p>
            <p className="text-sm text-gray-400">🏷 {event.category}</p>
            <p className="text-sm text-gray-300 mt-2">{event.description}</p>

            <div className="flex flex-col gap-2 mt-4">
              <button
                onClick={(e) => { e.stopPropagation(); openEditModal(event); }}
                className="bg-yellow-500 hover:bg-yellow-600 text-white text-sm px-3 py-1 rounded"
              >
                ✏️ Edit
              </button>

              <select
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleAddToDeck(event, e.target.value)}
                defaultValue=""
                className="bg-purple-600 text-white text-sm rounded px-3 py-2"
              >
                <option value="" disabled>➕ Add to Deck</option>
                {classData.decks.map(deck => (
                  <option key={deck.title} value={deck.title}>{deck.title}</option>
                ))}
              </select>

              <button
                onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event._id); }}
                className="text-red-400 hover:text-red-600 text-xs mt-2"
              >
                ❌ Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Summary Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-gray-900 p-6 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl relative">
            <button onClick={() => { setSelectedEvent(null); setInsights(null); }} className="absolute top-3 right-4 text-white text-xl font-bold">✖</button>
            <h3 className="text-2xl font-bold text-blue-300 mb-2">{selectedEvent.title}</h3>
            <p className="text-sm text-gray-400 mb-1">📅 {selectedEvent.year}</p>
            <p className="text-sm text-gray-400 mb-1">🏷 {selectedEvent.category}</p>
            <p className="text-gray-300 text-sm mt-3">{selectedEvent.description}</p>

            {selectedEvent.summary && (
              <>
                <h4 className="text-lg font-semibold text-purple-300 mt-6">📝 Teacher Summary</h4>
                <p className="text-sm text-gray-300 whitespace-pre-wrap mt-2">{selectedEvent.summary}</p>
              </>
            )}

            {!selectedEvent.disableAI && loadingSummary && (
              <p className="text-gray-400 mt-4">⏳ Loading AI insights...</p>
            )}

            {!selectedEvent.disableAI && insights?.ai_summary && (
              <>
                <h4 className="text-sm font-semibold text-purple-300 mt-4">🔎 AI Summary</h4>
                <p className="text-sm text-gray-300 whitespace-pre-wrap">{insights.ai_summary}</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingEvent && editForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-gray-900 p-6 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl relative">
            <button onClick={() => setEditingEvent(null)} className="absolute top-3 right-4 text-white text-xl font-bold">✖</button>

            <form onSubmit={handleSaveEdit} className="space-y-4">
              <input type="text" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}  placeholder="Title" className="w-full px-4 py-2 rounded bg-gray-700" required />
              <input type="number" value={editForm.year} onChange={(e) => setEditForm({ ...editForm, year: e.target.value })}   placeholder="Year (e.g. 1945)" className="w-full px-4 py-2 rounded bg-gray-700" required />
              <input type="text" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}    placeholder="Category (e.g. Politics, War)" className="w-full px-4 py-2 rounded bg-gray-700" />
              <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}   placeholder="Short Description" className="w-full px-4 py-2 rounded bg-gray-700" rows="2" />
              <textarea value={editForm.summary} onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })}     placeholder="Teacher Summary (optional)" className="w-full px-4 py-2 rounded bg-gray-700" rows="4" />
              {editForm.links.map((link, idx) => (
                <input key={idx} value={link} onChange={(e) => {
                  const updated = [...editForm.links];
                  updated[idx] = e.target.value;
                  setEditForm({ ...editForm, links: updated });
                }}       placeholder={`Link ${idx + 1}`} className="w-full mt-2 px-4 py-2 rounded bg-gray-700" />
              ))}
              <button type="button" onClick={() => setEditForm(prev => ({ ...prev, links: [...prev.links, ''] }))} className="text-blue-400 hover:underline text-sm">➕ Add another link</button>
              <label className="flex items-center gap-2 text-sm mt-4">
                <input type="checkbox" checked={editForm.disableAI} onChange={(e) => setEditForm({ ...editForm, disableAI: e.target.checked })} />
                Turn off AI summary
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editForm.bulletPointsSummary} onChange={(e) => setEditForm({ ...editForm, bulletPointsSummary: e.target.checked })} />
                Summarize with Bullet Points
              </label>
              <button type="submit" className="w-full bg-green-600 hover:bg-green-700 py-2 rounded-lg font-semibold mt-4">
                ✅ Save Changes
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManager;
